import { observer, useLocalObservable } from 'mobx-react';
import { useEffect, useId, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import IconButton, { ICON_BUTTON_ICONS } from '../../buttons/IconButton.jsx';
import ContextMenu from '../../common/ContextMenu';
import pubSub from '../../../utils/pubsub';

export const AGENT_ICONS = {
  SIDEBAR_COLLAPSE: '/icons/agent/sidebar-collapse.svg',
  FAVORITE: '/icons/agent/favorites_blue.svg'
};

const AgentSidebarMenuEntry = observer(
  ({
    label = 'Menu item',
    icon = ICON_BUTTON_ICONS.STAR,
    withBorder = false,
    wrapClassName = '',
    onClick = () => {},
    tooltipText = '',
    tooltipPosition = 'right',
    showTooltip = false,
    isHighlighted = false,
    showContextMenu = false,
    contextMenuItems = [],
    onContextMenuAction = () => {},
    onRename = () => {},
    isFavorite = false,
    chatUrl = '' // URL for "Open in new tab" right-click menu
  }) => {
    const menuEntryId = useId();
    const contextMenuRef = useRef(null);
    const inputRef = useRef(null);
    const savingRef = useRef(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(label);

    // Auto-focus and select text when editing starts
    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [isEditing]);

    // Update editValue when label changes
    useEffect(() => {
      setEditValue(label);
    }, [label]);

    const state = useLocalObservable(() => ({
      isContextMenuOpen: false,
      isLinkContextMenuOpen: false,
      toggleContextMenu: (e) => {
        e?.stopPropagation?.();
        state.isContextMenuOpen = !state.isContextMenuOpen;
      },
      closeContextMenu: () => {
        state.isContextMenuOpen = false;
      },
      toggleLinkContextMenu: () => {
        state.isLinkContextMenuOpen = !state.isLinkContextMenuOpen;
      },
      closeLinkContextMenu: () => {
        state.isLinkContextMenuOpen = false;
      }
    }));

    // Close link context menu on click outside or when another menu opens
    useEffect(() => {
      const handleClickOutside = () => {
        if (state.isLinkContextMenuOpen) {
          state.closeLinkContextMenu();
        }
      };

      const handlePubSubContextClick = (payload) => {
        if (payload?.event === 'sidebar-link-context-menu-trigger' && state.isLinkContextMenuOpen) {
          state.closeLinkContextMenu();
        }
      };

      pubSub.subscribe(handlePubSubContextClick);
      document.addEventListener('click', handleClickOutside);

      return () => {
        pubSub.unsubscribe(handlePubSubContextClick);
        document.removeEventListener('click', handleClickOutside);
      };
    }, []);

    const handleRightClick = (e) => {
      if (!chatUrl) return; // No URL, no right-click menu

      e.preventDefault();
      e.stopPropagation();
      pubSub.publish({ event: 'sidebar-link-context-menu-trigger' });
      state.toggleLinkContextMenu();
    };

    const handleOpenInNewTab = () => {
      if (chatUrl) {
        window.open(chatUrl, '_blank');
        state.closeLinkContextMenu();
      }
    };

    const className = wrapClassName
      ? `agent-sidebar-menu-entry ${wrapClassName}`
      : 'agent-sidebar-menu-entry';
    const isIconOnly = !label || label.trim() === '';
    let finalClassName = isIconOnly ? `${className} icon-only` : className;

    if (isHighlighted) {
      finalClassName += ' highlighted';
    }

    const handleStartRename = () => {
      setEditValue(label);
      setIsEditing(true);
      state.closeContextMenu();
    };

    const handleSaveRename = async () => {
      if (savingRef.current) return; // Prevent concurrent saves
      savingRef.current = true;

      const newValue = editValue.trim();
      const oldValue = label;

      // Early exit if no change or empty value
      if (!newValue || newValue === oldValue) {
        setIsEditing(false);
        savingRef.current = false;
        return;
      }

      // Optimistic update: exit edit mode immediately (non-blocking)
      setIsEditing(false);

      try {
        // Background API call
        await onRename(newValue);
      } catch (error) {
        console.error('Failed to rename conversation:', error);
        // Rollback: re-enter edit mode with old value so user can retry
        setEditValue(oldValue);
        setIsEditing(true);
      } finally {
        savingRef.current = false; // Always reset, even on error
      }
    };

    const handleCancelRename = () => {
      setEditValue(label);
      setIsEditing(false);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveRename();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancelRename();
      }
    };

    const handleInputClick = (e) => {
      e.stopPropagation();
    };

    const handleContextMenuAction = (action, ...args) => {
      // Intercept rename action to trigger inline editing
      if (action === 'rename') {
        handleStartRename();
      } else {
        onContextMenuAction(action, ...args);
        state.closeContextMenu();
      }
    };

    const menuItems = contextMenuItems.map((item) => ({
      ...item,
      onClick: () => handleContextMenuAction(item.action, item)
    }));

    // Build linkContextMenu for "Open in new tab" if chatUrl is provided
    const linkContextMenuItems = chatUrl
      ? [
          {
            icon: ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT,
            text: 'Open in new tab',
            onClick: handleOpenInNewTab
          }
        ]
      : [];

    if (!showContextMenu) {
      // Simple case - no context menu (but may have link context menu)
      return (
        <IconButton
          icon={icon}
          innerText={label}
          withBorder={withBorder}
          wrapClassName={finalClassName}
          onClick={onClick}
          tooltipText={showTooltip ? tooltipText : ''}
          tooltipPosition={tooltipPosition}
          showTooltipArrow={true}
          linkContextMenu={linkContextMenuItems}
        />
      );
    }

    // Complex case - with integrated context menu
    const baseClassName = 'agent-sidebar-menu-entry';
    return (
      <div className={`${baseClassName}-with-context`} style={{ position: 'relative' }}>
        <div
          id={menuEntryId}
          className={`${baseClassName}-container ${isHighlighted ? 'highlighted' : ''}`}
          onContextMenu={handleRightClick}
        >
          {/* Main chat entry content */}
          <div className={`${baseClassName}-main`} onClick={isEditing ? undefined : onClick}>
            <div className={`${baseClassName}-icon`}>
              <img src={icon} alt="" />
            </div>
            <div className={`${baseClassName}-text`}>
              {isEditing ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleSaveRename}
                  onClick={handleInputClick}
                  className="agent-sidebar-menu-entry-text-input"
                />
              ) : (
                label
              )}
            </div>
          </div>

          {/* Right-side actions: Favorite icon OR context menu (on hover) */}
          <div className={`${baseClassName}-actions`}>
            {/* Favorite bookmark icon (aligned right, hidden on hover) */}
            {isFavorite && (
              <div className={`${baseClassName}-favorite-indicator`}>
                <img src={AGENT_ICONS.FAVORITE} alt="Favorited" title="Favorited" />
              </div>
            )}
            {/* Ellipsis button (shown on hover, replaces favorite icon) */}
            <div
              className={`${baseClassName}-context-trigger`}
              ref={contextMenuRef}
              onClick={state.toggleContextMenu}
            >
              <img src={ICON_BUTTON_ICONS.THREE_DOTS_VERTICAL} alt="More actions" />
            </div>
          </div>
        </div>

        <ContextMenu
          isOpen={state.isContextMenuOpen}
          onClose={state.closeContextMenu}
          triggerElement={contextMenuRef.current}
          menuItems={menuItems}
        />

        {/* Right-click "Open in new tab" tooltip menu */}
        {chatUrl && state.isLinkContextMenuOpen && (
          <Tooltip
            anchorSelect={`#${CSS.escape(menuEntryId)}`}
            multiline={true}
            variant="dark"
            opacity={1}
            place="bottom"
            clickable={true}
            isOpen={true}
            className="icon-button-tooltip-context-link-menu"
          >
            <div className="context-link-menu-item" onClick={handleOpenInNewTab}>
              <div className="left-side">
                <div className="context-link-icon-wrap">
                  <img src={ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT} alt="" />
                </div>
                <div className="context-link-text">Open in new tab</div>
              </div>
            </div>
          </Tooltip>
        )}
      </div>
    );
  }
);

export default AgentSidebarMenuEntry;
